import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoAddOutline } from "react-icons/io5";
import { HiOutlineSearch } from "react-icons/hi";
import { MdClear, MdDriveFileRenameOutline, MdPreview } from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";

import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import RightPanelLayout from "../Layout/RightPanelLayout";
import Switcher from "../Utilities/AlertSwitch/Switcher";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";

import styles from "./style/ReleaseNotesList.module.scss";
import ReleaseNotePreviewModal from "./ReleaseNotePreviewModal";
import {
  fetchReleaseNotesList,
  fetchReleaseNoteById,
  patchReleaseNotePublish,
  deleteReleaseNote,
} from "./releaseNotesApi";
import {
  extractImageUrlsFromReleaseNoteDetail,
  formatPublishedAt,
  normalizeReleaseNoteListRow,
  normalizeTagsFromApi,
  toLocalDatetimeInput,
} from "./releaseNotesTransforms";

const LIMIT = 10;

const EMPTY_DISPLAY = "未設定";

const ReleaseNotesList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");

  const [publishBusyId, setPublishBusyId] = useState(null);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDetail, setPreviewDetail] = useState(null);
  const [previewLoadingId, setPreviewLoadingId] = useState(null);

  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const showToast = (title, error = false) => {
    setToastState({ show: true, title, subtitle: "", error });
    window.setTimeout(() => {
      setToastState((s) => ({ ...s, show: false }));
    }, 3200);
  };

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await fetchReleaseNotesList({
        search: searchApplied,
        page,
        limit: LIMIT,
      });
      const apiRows = data?.rows ?? [];
      setRows(apiRows.map(normalizeReleaseNoteListRow));
      setTotal(Number(data?.total) || 0);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setListLoading(false);
    }
  }, [page, searchApplied]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total],
  );

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const handlePublishToggle = async (item, nextChecked) => {
    if (publishBusyId === item.id) return;
    setPublishBusyId(item.id);
    try {
      await patchReleaseNotePublish(item.id, { is_published: nextChecked });
      setRows((prev) =>
        prev.map((r) =>
          r.id === item.id ? { ...r, isPublished: nextChecked } : r,
        ),
      );
      if (nextChecked) {
        showToast(`已成功上架發行日記「${item.title || EMPTY_DISPLAY}」。`);
      } else {
        showToast(`已成功下架發行日記「${item.title || EMPTY_DISPLAY}」。`);
      }
    } catch {
      showToast("儲存失敗請重試", true);
    } finally {
      setPublishBusyId(null);
    }
  };

  const handleOpenPreview = async (id) => {
    setPreviewLoadingId(id);
    try {
      const data = await fetchReleaseNoteById(id);
      setPreviewDetail(data);
      setPreviewOpen(true);
    } catch {
      showToast("載入預覽失敗請重試", true);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewDetail(null);
  };

  const handleDelete = async () => {
    if (!deleteTargetId || deleteSubmitting) return;
    setDeleteSubmitting(true);
    try {
      await deleteReleaseNote(deleteTargetId);
      setDeleteTargetId(null);
      showToast("已刪除發行日記", false);
      await loadList();
    } catch {
      showToast("刪除失敗請重試", true);
    } finally {
      setDeleteSubmitting(false);
    }
  };

  return (
    <RightPanelLayout title='發行日記'>
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>發行日記列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type='button'
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label='搜尋'
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type='search'
              className={styles.searchBarInput}
              value={searchInput}
              placeholder='搜尋公告名稱'
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label='搜尋公告名稱'
            />
            {searchInput ? (
              <button
                type='button'
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label='清除搜尋'
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type='primary' title='搜尋' onClick={applyListSearch} />
          <Button
            type='primary'
            title='新增'
            icon={<IoAddOutline fontSize={20} />}
            onClick={() => navigate("/dashboard/tdp/release-notes/new")}
          />
        </div>
      </div>

      {!listLoading && total > 0 ? (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>標題</th>
              <th>發佈時間</th>
              <th>狀態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={4} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}

            {!listLoading && rows.length === 0 ? (
              <tr>
                <td colSpan={4} className={styles.tableEmptyCell}>
                  尚無發行日記資料
                </td>
              </tr>
            ) : null}

            {!listLoading
              ? rows.map((item) => (
                  <tr key={item.id}>
                    <td>{item.title || EMPTY_DISPLAY}</td>
                    <td>
                      <span className={item.publishedAt ? undefined : styles.placeholderText}>
                        {item.publishedAt ? formatPublishedAt(item.publishedAt) : EMPTY_DISPLAY}
                      </span>
                    </td>
                    <td>
                      <div className={styles.switchWrap}>
                        <Switcher
                          isChecked={item.isPublished}
                          disabled={publishBusyId === item.id}
                          onChange={(e) => {
                            handlePublishToggle(item, e.target.checked);
                          }}
                          color={{ on: "#5856D6", off: "#5856D64D" }}
                          width={33}
                          height={20}
                        />
                      </div>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          type='button'
                          className={styles.viewButton}
                          onClick={() => handleOpenPreview(item.id)}
                          disabled={previewLoadingId === item.id}
                          aria-label='預覽發行日記'
                        >
                          <MdPreview />
                        </button>
                        <button
                          type='button'
                          className={styles.editButton}
                          onClick={() =>
                            navigate(
                              `/dashboard/tdp/release-notes/${item.id}/edit`,
                            )
                          }
                          aria-label='編輯發行日記'
                        >
                          <MdDriveFileRenameOutline />
                        </button>
                        <button
                          type='button'
                          className={styles.deleteButton}
                          onClick={() => setDeleteTargetId(item.id)}
                          aria-label='刪除發行日記'
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              : null}
          </tbody>
        </table>
      </div>

      <Alert
        isOpen={deleteTargetId != null}
        onClose={() => !deleteSubmitting && setDeleteTargetId(null)}
        onConfirm={handleDelete}
        leftButtonText='取消'
        rightButtonText={deleteSubmitting ? "刪除中…" : "刪除"}
        mainTitle='確認刪除發行日記？'
        subTitle='刪除後無法復原。'
        isWarning
      />

      <ReleaseNotePreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreview}
        title={previewDetail?.title ?? ""}
        subtitle={previewDetail?.subtitle ?? ""}
        publishedAtRaw={toLocalDatetimeInput(
          previewDetail?.published_at ?? "",
        )}
        contentHtml={previewDetail?.content ?? ""}
        tags={normalizeTagsFromApi(previewDetail?.meta_json?.tags ?? previewDetail?.tags)}
        imageUrls={extractImageUrlsFromReleaseNoteDetail(previewDetail)}
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default ReleaseNotesList;

